/**
 * @module AnimatedHero
 * Split-image hero with lasers, shimmer, glass panel, and light parallax.
 * Motion stays on opacity/transform; effects pause off-screen or when the tab hides.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PUBLIC_PHONE } from '@lib/businessContact';

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function AnimatedHero({ heroImage, heroImageSecondary }) {
  const headerRef = useRef(null);
  const imageRef = useRef(null);
  const [fxActive, setFxActive] = useState(false);

  useEffect(() => {
    const root = headerRef.current;
    if (!root || prefersReducedMotion()) {
      setFxActive(false);
      return undefined;
    }

    let inView = true;
    let visible = document.visibilityState === 'visible';

    const sync = () => setFxActive(inView && visible);

    const io = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        sync();
      },
      { threshold: 0.12 }
    );
    io.observe(root);

    const onVisibility = () => {
      visible = document.visibilityState === 'visible';
      sync();
    };
    document.addEventListener('visibilitychange', onVisibility);
    sync();

    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Lightweight parallax — one rAF write, no ScrollTrigger/GSAP on the hero.
  useEffect(() => {
    const root = headerRef.current;
    const layer = imageRef.current;
    if (!fxActive || !root || !layer || prefersReducedMotion()) {
      if (layer) layer.style.transform = '';
      return undefined;
    }

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = root.getBoundingClientRect();
      const h = rect.height || 1;
      const progress = Math.min(1, Math.max(0, -rect.top / h));
      layer.style.transform = `translate3d(0, ${(progress * 5).toFixed(2)}%, 0)`;
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
      layer.style.transform = '';
    };
  }, [fxActive]);

  return (
    <header
      ref={headerRef}
      id="home"
      className={`relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-20 ${
        fxActive ? 'hero-fx-active' : ''
      }`}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 grid grid-cols-2 scale-[1.06] origin-center"
          ref={imageRef}
        >
          <img
            src={heroImage}
            alt="Illustrative neon lighting in an indoor trampoline venue, not a documented Avision project photo"
            width="640"
            height="640"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <img
            src={heroImageSecondary}
            alt="Illustrative roller rink with colored stage lighting, not a documented Avision project photo"
            width="640"
            height="640"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E1B4B]/60 to-[#1E1B4B]/40 z-[1]" />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F23]/80 via-[#1E1B4B]/60 to-[#0F0F23]/90 z-[1]" />

      <div className="laser-overlay z-[2]" aria-hidden="true">
        <div className="laser-track laser-track--a">
          <div className="laser-beam" />
        </div>
        <div className="laser-track laser-track--b">
          <div className="laser-beam" />
        </div>
        <div className="laser-track laser-track--c">
          <div className="laser-beam" />
        </div>
      </div>

      <div className="hero-panel relative z-10 max-w-4xl mx-auto p-4 sm:p-8 md:p-12 text-center mt-2 sm:mt-4 border border-white/10 rounded-xl sm:rounded-2xl">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-2 sm:mb-4 text-white leading-tight glow-text">
          Commercial & specialty lighting
          <br className="block" />
          <span className="hero-shimmer-text relative inline-block">
            <span className="hero-shimmer-base">across South Florida</span>
            <span className="hero-shimmer-pulse" aria-hidden="true">
              across South Florida
            </span>
          </span>
        </h1>

        <p className="text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4 text-gray-100 max-w-3xl mx-auto">
          Serving Palm Beach, Broward, and Miami-Dade counties with permanent commercial LED,
          RGBW and DMX, sports, recreation, and architectural lighting — installed at the
          facility, not sold as event rental.
        </p>

        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-4 sm:mb-6">
          {[
            { icon: 'fa-map-marker-alt', text: 'Palm Beach · Broward · Miami-Dade' },
            { icon: 'fa-bolt', text: 'RGBW & DMX systems' },
            { icon: 'fa-lightbulb', text: 'LED retrofits' },
            { icon: 'fa-building', text: 'Permanent installations' }
          ].map((item) => (
            <div
              key={item.text}
              className="hero-chip flex items-center px-3 sm:px-4 py-1.5 sm:py-2"
            >
              <i
                className={`fas ${item.icon} text-[#06B6D4] mr-1.5 sm:mr-2 text-sm sm:text-base`}
                aria-hidden="true"
              />
              <span className="text-white/90 text-xs sm:text-sm font-medium whitespace-nowrap">
                {item.text}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 max-w-2xl mx-auto">
          <div className="w-full sm:w-auto space-y-3">
            <Link
              to="/contact"
              className="btn-primary flex items-center justify-center w-full px-8 py-4 text-lg sm:text-xl font-bold"
            >
              <i className="fas fa-file-signature mr-3" aria-hidden="true" />
              Request a Lighting Assessment
            </Link>
            <a
              href={PUBLIC_PHONE.sms}
              className="flex items-center justify-center w-full px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-colors text-sm sm:text-base font-medium min-h-[48px]"
            >
              <i className="fas fa-sms mr-2 text-[#EC4899]" aria-hidden="true" />
              Text {PUBLIC_PHONE.display}
            </a>
            <Link
              to="/projects"
              className="btn-secondary flex items-center justify-center w-full px-8 py-3 text-base font-semibold bg-[#06B6D4]/10 hover:bg-[#06B6D4]/20 border border-[#06B6D4] text-white rounded-xl"
            >
              <i className="fas fa-images mr-3 text-[#06B6D4]" aria-hidden="true" />
              View Lighting Projects
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

AnimatedHero.propTypes = {
  heroImage: PropTypes.string.isRequired,
  heroImageSecondary: PropTypes.string.isRequired,
  introComplete: PropTypes.bool
};
